import React, { useState, useEffect } from "react";
import { Crown, X, AlertTriangle, Clock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const UpgradeBanner = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [subStatus, setSubStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${BACKEND_URL}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSubStatus(await res.json());
      } catch (e) { /* ignore */ }
    };
    load();
  }, [token]);

  if (!subStatus || dismissed) return null;

  const isPremium = subStatus.plan === "monthly" || subStatus.plan === "annual";
  if (isPremium) return null;

  const isTrial = subStatus.plan === "trial";
  const daysLeft = subStatus.days_left ?? 99;
  const isUrgent = isTrial && daysLeft <= 3;

  if (isUrgent) {
    return (
      <div
        className="mx-4 mt-2 rounded-lg overflow-hidden border border-red-300 shadow-md"
        data-testid="trial-expiry-banner"
      >
        <div className="bg-gradient-to-r from-red-600 to-orange-500 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <AlertTriangle className="h-5 w-5 text-white" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-yellow-300 rounded-full animate-ping" />
            </div>
            <div>
              <p className="text-white font-bold text-sm leading-tight">
                {daysLeft === 0
                  ? "Trial expires today!"
                  : daysLeft === 1
                  ? "Trial ends tomorrow!"
                  : `Only ${daysLeft} days left in your trial`}
              </p>
              <p className="text-red-100 text-xs mt-0.5">
                Subscribe now to keep all your data & features
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              className="bg-white text-red-600 font-bold text-xs px-3 py-1.5 rounded-md hover:bg-red-50 transition-colors"
              onClick={() => navigate("/subscription")}
              data-testid="trial-expiry-subscribe-btn"
            >
              Subscribe Now
            </button>
            <button onClick={() => setDismissed(true)} className="text-red-200 hover:text-white">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs mx-4 mt-2 ${
      isTrial ? "bg-blue-50 border border-blue-200" : "bg-amber-50 border border-amber-200"
    }`} data-testid="upgrade-banner">
      <div className="flex items-center gap-2">
        {isTrial
          ? <Clock className="h-3.5 w-3.5 text-blue-500" />
          : <Crown className="h-3.5 w-3.5 text-amber-500" />
        }
        <span className={isTrial ? "text-blue-700" : "text-amber-700"}>
          {isTrial
            ? `Free trial — ${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`
            : "Upgrade to Premium for full access"
          }
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          className={`font-semibold px-2 py-0.5 rounded ${
            isTrial ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-amber-500 text-white hover:bg-amber-600"
          }`}
          onClick={() => navigate("/subscription")}
          data-testid="upgrade-btn"
        >
          {isTrial ? "View Plans" : "Upgrade"}
        </button>
        <button onClick={() => setDismissed(true)} className="text-gray-400 hover:text-gray-500">
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};

export const AdPlaceholder = ({ position = "banner" }) => {
  const { token } = useAuth();
  const [subStatus, setSubStatus] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${BACKEND_URL}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSubStatus(await res.json());
      } catch (e) { /* ignore */ }
    };
    load();
  }, [token]);

  // Only show ads for free plan users
  if (!subStatus || subStatus.plan !== "free") return null;

  return (
    <div
      className="bg-gray-100 border border-dashed border-gray-300 rounded-lg flex items-center justify-center mx-4 my-2"
      style={{ height: position === "banner" ? 60 : 90 }}
      data-testid={`ad-placeholder-${position}`}
    >
      <span className="text-xs text-gray-400 font-medium">Ad Space</span>
    </div>
  );
};
